import { useEffect, useState } from "react";
import { Trash2, Pencil, X, Repeat, Plus } from "lucide-react";
import { Header } from "@/components/Header";
import { SpreadsheetImport } from "@/components/SpreadsheetImport";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = {
  title: "",
  summary: "",
  description: "",
  category: "Meetup",
  tags: "",
  image_url: "https://images.unsplash.com/photo-1563461660947-507ef49e9c47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  start_at: "",
  venue_name: "",
  neighborhood: "Downtown",
  address: "",
  price: "Free",
  capacity: 50,
  host_name: "",
  host_org: "",
};

const field =
  "w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 outline-none transition-colors duration-200 focus:border-neutral-900";

export default function Organize() {
  const { user, loading, login } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [recurring, setRecurring] = useState(false);
  const [pattern, setPattern] = useState({ type: "weekly", until: "", weekday: 2, week_of_month: 1, dates: "" });
  const [seriesPreview, setSeriesPreview] = useState(null);
  const [scopeAsk, setScopeAsk] = useState(null);

  const load = () => apiFetch("/events").then(setEvents).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const buildPayload = () => ({
    ...form,
    capacity: Number(form.capacity) || 50,
    tags: String(form.tags).split(",").map((t) => t.trim()).filter(Boolean),
    start_at: form.start_at.length === 16 ? `${form.start_at}:00` : form.start_at,
    sponsors: sponsors.filter((s) => s.name.trim()),
  });

  const buildPattern = () => ({
    type: pattern.type,
    until: pattern.until || undefined,
    weekday: Number(pattern.weekday),
    week_of_month: Number(pattern.week_of_month),
    dates: String(pattern.dates).split(",").map((d) => d.trim()).filter(Boolean),
  });

  const previewSeries = async () => {
    try {
      const res = await apiFetch("/series/preview", {
        method: "POST",
        body: JSON.stringify({ template: buildPayload(), pattern: buildPattern() }),
      });
      setSeriesPreview(res);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveEvent = async (scope) => {
    setSaving(true);
    const payload = buildPayload();
    try {
      if (editing) {
        await apiFetch(`/events/${editing}?scope=${scope || "this"}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
        toast.success(scope === "future" ? "Series updated from this date onward" : "Event updated");
      } else if (recurring) {
        const res = await apiFetch("/series", {
          method: "POST",
          body: JSON.stringify({ template: payload, pattern: buildPattern() }),
        });
        toast.success(`Series created · ${res.created} dates published`);
      } else {
        await apiFetch("/events", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Event published");
      }
      setForm(EMPTY);
      setSponsors([]);
      setEditing(null);
      setRecurring(false);
      setSeriesPreview(null);
      load();
    } catch (err) {
      toast.error(err.message);
    }
    setSaving(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const current = events.find((ev) => ev.event_id === editing);
    if (editing && current?.series_id) {
      setScopeAsk({ mode: "edit", event: current });
      return;
    }
    saveEvent("this");
  };

  const startEdit = (ev) => {
    setEditing(ev.event_id);
    setForm({ ...ev, tags: (ev.tags || []).join(", "), start_at: ev.start_at.slice(0, 16) });
    setSponsors(ev.sponsors || []);
    setRecurring(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (ev, scope) => {
    if (ev.series_id && !scope) {
      setScopeAsk({ mode: "delete", event: ev });
      return;
    }
    try {
      const res = await apiFetch(`/events/${ev.event_id}?scope=${scope || "this"}`, { method: "DELETE" });
      toast.success(res.deleted > 1 ? `${res.deleted} dates removed` : "Event removed");
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="min-h-screen bg-white" />;

  if (!user || user.role !== "leader") {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="mx-auto max-w-2xl px-4 py-24 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900">Leaders only</h1>
          <p className="mt-3 text-neutral-600" data-testid="organize-denied">
            Adding listings to the Portland Tech Calendar is limited to invited community leaders.
          </p>
          {!user && (
            <button
              onClick={login}
              data-testid="organize-login"
              className="mt-6 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              Continue with Google
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900 sm:text-4xl">
          {editing ? "Edit listing" : "Add a listing"}
        </h1>
        <p className="mt-2 text-sm text-neutral-500">Signed in as {user.email} · community leader</p>

        <form onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" data-testid="event-form">
          <input required data-testid="input-title" className={`${field} sm:col-span-2`} placeholder="Event title" value={form.title} onChange={set("title")} />
          <input required data-testid="input-summary" className={`${field} sm:col-span-2`} placeholder="One line summary" value={form.summary} onChange={set("summary")} />
          <textarea data-testid="input-description" rows={4} className={`${field} sm:col-span-2`} placeholder="Full description" value={form.description} onChange={set("description")} />
          <input required data-testid="input-category" className={field} placeholder="Category (Meetup, Workshop…)" value={form.category} onChange={set("category")} />
          <input data-testid="input-tags" className={field} placeholder="Tags, comma separated" value={form.tags} onChange={set("tags")} />
          <input required type="datetime-local" data-testid="input-start" className={field} value={form.start_at} onChange={set("start_at")} />
          <input data-testid="input-price" className={field} placeholder="Price (Free, $10)" value={form.price} onChange={set("price")} />
          <input required data-testid="input-venue" className={field} placeholder="Venue name" value={form.venue_name} onChange={set("venue_name")} />
          <input data-testid="input-neighborhood" className={field} placeholder="Neighborhood" value={form.neighborhood} onChange={set("neighborhood")} />
          <input data-testid="input-address" className={`${field} sm:col-span-2`} placeholder="Address" value={form.address} onChange={set("address")} />
          <input data-testid="input-image" className={`${field} sm:col-span-2`} placeholder="Image URL" value={form.image_url} onChange={set("image_url")} />
          <input data-testid="input-host" className={field} placeholder="Host name" value={form.host_name} onChange={set("host_name")} />
          <input data-testid="input-org" className={field} placeholder="Host organization" value={form.host_org} onChange={set("host_org")} />
          <input data-testid="input-capacity" type="number" className={field} placeholder="Capacity" value={form.capacity} onChange={set("capacity")} />

          <div className="sm:col-span-2" data-testid="sponsors-editor">
            <p className="mb-2 text-sm font-bold text-neutral-900">Sponsors</p>
            <p className="mb-3 text-xs text-neutral-500">Listed on the event page under the description.</p>
            {sponsors.map((s, i) => (
              <div key={i} className="mb-2 flex gap-2">
                <input
                  data-testid={`sponsor-name-${i}`}
                  className={field}
                  placeholder="Sponsor name"
                  value={s.name}
                  onChange={(e) =>
                    setSponsors(sponsors.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))
                  }
                />
                <button
                  type="button"
                  data-testid={`sponsor-remove-${i}`}
                  onClick={() => setSponsors(sponsors.filter((_, j) => j !== i))}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-lg transition-colors duration-200 hover:bg-neutral-100"
                >
                  <X className="h-4 w-4 text-neutral-500" />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="sponsor-add"
              onClick={() => setSponsors([...sponsors, { name: "", logo_url: "" }])}
              className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              <Plus className="h-4 w-4" /> Add sponsor
            </button>
          </div>

          {!editing && (
            <div className="rounded-xl border border-neutral-200 p-5 sm:col-span-2" data-testid="recurrence-panel">
              <label className="flex items-center gap-2.5 text-sm font-bold text-neutral-900">
                <input
                  type="checkbox"
                  data-testid="recurring-toggle"
                  checked={recurring}
                  onChange={(e) => setRecurring(e.target.checked)}
                  className="h-4 w-4 accent-[#65A30D]"
                />
                <Repeat className="h-4 w-4" /> This is a repeating meetup
              </label>

              {recurring && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <select
                    data-testid="pattern-type"
                    className={field}
                    value={pattern.type}
                    onChange={(e) => setPattern({ ...pattern, type: e.target.value })}
                  >
                    <option value="weekly">Every week</option>
                    <option value="biweekly">Every two weeks</option>
                    <option value="monthly_weekday">Monthly on a weekday</option>
                    <option value="custom">Specific dates I pick</option>
                  </select>

                  {pattern.type !== "custom" && (
                    <input
                      type="date"
                      data-testid="pattern-until"
                      className={field}
                      value={pattern.until}
                      onChange={(e) => setPattern({ ...pattern, until: e.target.value })}
                      placeholder="Repeat until"
                    />
                  )}

                  {pattern.type === "monthly_weekday" && (
                    <>
                      <select
                        data-testid="pattern-week"
                        className={field}
                        value={pattern.week_of_month}
                        onChange={(e) => setPattern({ ...pattern, week_of_month: e.target.value })}
                      >
                        <option value={1}>First</option>
                        <option value={2}>Second</option>
                        <option value={3}>Third</option>
                        <option value={4}>Fourth</option>
                        <option value={-1}>Last</option>
                      </select>
                      <select
                        data-testid="pattern-weekday"
                        className={field}
                        value={pattern.weekday}
                        onChange={(e) => setPattern({ ...pattern, weekday: e.target.value })}
                      >
                        {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => (
                          <option key={d} value={i}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </>
                  )}

                  {pattern.type === "custom" && (
                    <input
                      data-testid="pattern-dates"
                      className={`${field} sm:col-span-2`}
                      placeholder="2026-09-08, 2026-09-22, 2026-10-06"
                      value={pattern.dates}
                      onChange={(e) => setPattern({ ...pattern, dates: e.target.value })}
                    />
                  )}

                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      onClick={previewSeries}
                      data-testid="preview-series-btn"
                      className="rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
                    >
                      Preview dates
                    </button>
                    {seriesPreview && (
                      <p className="mt-3 text-sm text-neutral-600" data-testid="series-preview">
                        {seriesPreview.count} date(s):{" "}
                        {seriesPreview.dates
                          .slice(0, 8)
                          .map((d) => new Date(d).toLocaleDateString())
                          .join(", ")}
                        {seriesPreview.count > 8 ? "…" : ""}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 sm:col-span-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-event"
              className="rounded-full bg-[#B8FF2E] px-6 py-3 text-sm font-bold text-[#14210A] transition-colors duration-200 hover:bg-[#A2E80B] disabled:opacity-60"
            >
              {editing ? "Save changes" : recurring ? "Publish series" : "Publish event"}
            </button>
            {editing && (
              <button
                type="button"
                data-testid="cancel-edit"
                onClick={() => {
                  setEditing(null);
                  setForm(EMPTY);
                  setSponsors([]);
                }}
                className="flex items-center gap-1.5 rounded-full border border-neutral-300 px-5 py-3 text-sm font-semibold text-neutral-700 transition-colors duration-200 hover:bg-neutral-50"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            )}
          </div>
        </form>

        <h2 className="mt-14 text-2xl font-bold tracking-tight text-neutral-900">All listings</h2>
        <ul className="mt-4 divide-y divide-neutral-200" data-testid="manage-list">
          {events.map((ev) => (
            <li key={ev.event_id} className="flex items-center gap-4 py-3">
              <img src={ev.image_url} alt="" className="h-12 w-16 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900">
                  {ev.title}
                  {ev.series_id && (
                    <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-neutral-600">
                      series
                    </span>
                  )}
                </p>
                <p className="text-xs text-neutral-500">
                  {new Date(ev.start_at).toLocaleDateString()} · {ev.venue_name}
                </p>
              </div>
              <button
                data-testid={`edit-${ev.event_id}`}
                onClick={() => startEdit(ev)}
                className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
              >
                <Pencil className="h-4 w-4 text-neutral-600" />
              </button>
              <button
                data-testid={`delete-${ev.event_id}`}
                onClick={() => remove(ev)}
                className="grid h-9 w-9 place-items-center rounded-full transition-colors duration-200 hover:bg-neutral-100"
              >
                <Trash2 className="h-4 w-4 text-[#4D7C0F]" />
              </button>
            </li>
          ))}
        </ul>

        <SpreadsheetImport onImported={load} />
      </div>

      <Dialog open={!!scopeAsk} onOpenChange={(o) => !o && setScopeAsk(null)}>
        <DialogContent className="rounded-2xl bg-white sm:max-w-md" data-testid="scope-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              {scopeAsk?.mode === "delete" ? "Remove which dates?" : "Apply changes to which dates?"}
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              "{scopeAsk?.event?.title}" is part of a repeating series.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 flex flex-col gap-2">
            <button
              data-testid="scope-this"
              onClick={() => {
                const ask = scopeAsk;
                setScopeAsk(null);
                ask.mode === "delete" ? remove(ask.event, "this") : saveEvent("this");
              }}
              className="rounded-full bg-neutral-900 px-5 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-neutral-700"
            >
              This date only
            </button>
            <button
              data-testid="scope-future"
              onClick={() => {
                const ask = scopeAsk;
                setScopeAsk(null);
                ask.mode === "delete" ? remove(ask.event, "future") : saveEvent("future");
              }}
              className="rounded-full border border-neutral-300 px-5 py-3 text-sm font-semibold text-neutral-800 transition-colors duration-200 hover:bg-neutral-50"
            >
              This and all future dates
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
